<template>
  <v-dialog :value="value" max-width="900" scrollable :persistent="saving" @input="$emit('input', $event)">
    <v-card v-if="action" class="assignment-details-card">
      <v-card-title class="d-flex align-center px-6 py-4">
        {{ title }}
        <v-spacer />
        <v-btn icon :disabled="saving" aria-label="Close assignment details" @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-6 assignment-details-body">
        <v-alert v-if="mode === 'assign' && action.assignmentStatus === 'rejected'" type="warning" text outlined data-cy="follow-up-rejected-by">
          <div class="font-weight-medium">{{ rejectingUser ? `${userName(rejectingUser)} has rejected this action` : 'This action was rejected' }}</div>
          <div v-if="action.rejectionReason" class="assignment-text mt-2">{{ action.rejectionReason }}</div>
        </v-alert>
        <v-alert v-if="error" type="error" text dense>{{ error }}</v-alert>
        <v-alert v-if="mode === 'reject'" type="info" text dense>
          This action will return to {{ userName(action.createdBy) }} in their Rejected section.
        </v-alert>
        <template v-if="mode === 'details'">
          <section class="details-section" aria-labelledby="assignment-action-heading">
            <h3 id="assignment-action-heading" class="details-heading">Action overview</h3>
            <div class="action-summary">
              <div class="subtitle-1 font-weight-medium assignment-text">{{ action.actionType.name }}</div>
              <div class="body-2 assignment-text mt-2">{{ action.actionType.description || 'No description provided.' }}</div>
            </div>
            <dl class="details-grid">
              <div class="detail-field"><dt>Status</dt><dd><v-chip small label dark :color="statusColor(action.status)">{{ statusLabel(action.status) }}</v-chip></dd></div>
              <div class="detail-field"><dt>Priority</dt><dd><v-chip small label dark :color="priorityColor(action.priority?.id)">{{ action.priority?.name || 'Not specified' }}</v-chip></dd></div>
              <div class="detail-field"><dt>Related record</dt><dd>{{ relatedRecordLabel }}</dd></div>
              <div class="detail-field"><dt>Due date and time</dt><dd>{{ detailDate(action.dueAt) }}</dd></div>
              <div class="detail-field detail-field-wide">
                <dt>Modules</dt>
                <dd class="details-module-list"><v-chip v-for="moduleId in actionModuleIds" :key="moduleId" small outlined color="primary">{{ moduleName(moduleId) }}</v-chip><span v-if="!actionModuleIds.length">Not specified</span></dd>
              </div>
            </dl>
          </section>
          <section class="details-section" aria-labelledby="assignment-owner-heading">
            <h3 id="assignment-owner-heading" class="details-heading">Assignment and ownership</h3>
            <dl class="details-grid">
              <div class="detail-field"><dt>Assigned by</dt><dd>{{ userName(action.createdBy) }}<div v-if="action.createdBy?.email && action.createdBy.email !== userName(action.createdBy)" class="detail-secondary">{{ action.createdBy.email }}</div></dd></div>
              <div class="detail-field"><dt>{{ action.assignmentStatus === 'rejected' ? 'Returned to' : 'Assigned to' }}</dt><dd>{{ userName(action.assignedTo) }}<div v-if="action.assignedTo?.email && action.assignedTo.email !== userName(action.assignedTo)" class="detail-secondary">{{ action.assignedTo.email }}</div></dd></div>
              <div class="detail-field detail-field-wide"><dt>Assignment status</dt><dd><v-chip small label outlined :color="assignmentColor">{{ assignmentLabel(action) }}</v-chip></dd></div>
            </dl>
          </section>
          <section class="details-section" aria-labelledby="assignment-dates-heading">
            <h3 id="assignment-dates-heading" class="details-heading">Dates</h3>
            <dl class="details-grid"><div v-for="date in detailDates" :key="date.label" class="detail-field"><dt>{{ date.label }}</dt><dd>{{ detailDate(date.value) }}</dd></div></dl>
          </section>
        </template>
        <section v-if="action.assignmentNote || (action.rejectionReason && mode !== 'assign')" class="details-section" aria-labelledby="assignment-notes-heading">
          <h3 id="assignment-notes-heading" class="details-heading">Notes</h3>
          <div v-if="action.assignmentNote" class="assignment-note"><div class="subtitle-2 mb-2">Assignment note</div><div class="assignment-text">{{ action.assignmentNote }}</div></div>
          <div v-if="action.rejectionReason && mode !== 'assign'" class="assignment-note rejection-note"><div class="subtitle-2 mb-2">Rejection reason</div><div class="assignment-text">{{ action.rejectionReason }}</div></div>
        </section>
        <v-textarea v-if="mode === 'reject'" v-model="reason" label="Reason for rejection" outlined rows="3" counter="1000" maxlength="1000" :disabled="saving" :rules="[v => !!v.trim() || 'Enter a reason.']" data-cy="follow-up-rejection-reason" />
        <template v-if="mode === 'assign'">
          <v-alert v-if="assigneesError" type="error" text dense>
            {{ assigneesError }} <v-btn small text color="error" @click="$emit('retry-assignees')">Try again</v-btn>
          </v-alert>
          <v-autocomplete v-model="assignedToId" :items="assignees" item-text="name" item-value="id" label="Assign to" outlined :loading="assigneesLoading" :disabled="saving" data-cy="follow-up-reassign-user" />
          <v-textarea v-model="assignmentNote" label="Assignment note (optional)" outlined rows="2" counter="2000" maxlength="2000" :disabled="saving" />
        </template>
        <section v-if="mode === 'details'" class="details-section mb-0" aria-labelledby="assignment-activity-heading">
          <div class="activity-section-heading"><h3 id="assignment-activity-heading" class="details-heading mb-0">Activity ({{ activityEvents.length }})</h3><span class="caption grey--text">Latest first</span></div>
          <ol class="activity-list">
            <li v-for="event in activityEvents" :key="event.id" class="activity-entry">
              <div class="activity-icon"><v-icon small color="primary">{{ activityIcon(event.type) }}</v-icon></div>
              <div class="activity-content">
                <div class="activity-entry-heading"><div class="font-weight-medium">{{ activityLabel(event.type) }}</div><time class="caption detail-secondary" :datetime="event.dateCreated">{{ detailDate(event.dateCreated) }}</time></div>
                <div class="caption detail-secondary mt-1">{{ userName(event.user) }}</div>
                <div class="body-2 assignment-text mt-2">{{ event.comment }}</div>
              </div>
            </li>
          </ol>
        </section>
      </v-card-text>
      <v-divider />
      <v-card-actions class="px-6 py-4 flex-wrap assignment-details-actions">
        <v-btn v-if="mode === 'details'" text color="primary" class="text-capitalize" @click="$emit('comments', action)">Comments ({{ (action.comments || []).length }})</v-btn>
        <v-spacer />
        <v-btn text class="text-capitalize" :disabled="saving" @click="$emit('input', false)">{{ mode === 'details' ? 'Close' : 'Cancel' }}</v-btn>
        <template v-if="!readOnly && mode === 'details' && canRespond">
          <v-btn outlined color="error" class="text-capitalize" :disabled="saving" @click="mode = 'reject'">Reject</v-btn>
          <v-btn color="primary" depressed class="text-capitalize" :loading="saving" @click="$emit('respond', { action, response: 'accept' })">Accept Action</v-btn>
        </template>
        <v-btn v-if="!readOnly && mode === 'details' && canReassign" color="primary" depressed class="text-capitalize" :disabled="saving" @click="mode = 'assign'; $emit('retry-assignees')">Reassign</v-btn>
        <v-btn v-if="!readOnly && mode === 'reject'" color="error" depressed class="text-capitalize" :loading="saving" :disabled="!reason.trim() || reason.length > 1000" @click="$emit('respond', { action, response: 'reject', reason: reason.trim() })" data-cy="follow-up-reject-confirm">Reject &amp; Return</v-btn>
        <v-btn v-if="!readOnly && mode === 'assign'" color="primary" depressed class="text-capitalize" :loading="saving" :disabled="!assignedToId || assignmentNote.length > 2000" @click="$emit('assign', { action, assignedToId, assignmentNote: assignmentNote.trim() })" data-cy="follow-up-reassign-confirm">Reassign</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import FormatMixin from '@/mixins/format.mixin';
import { assignmentLabel, assignmentStatus, canRespondToAssignment, canReassignAction } from './assignment-utils';

export default {
  name: 'FollowUpAssignmentDialog',
  mixins: [FormatMixin],
  props: {
    value: Boolean, action: Object, initialMode: { type: String, default: 'details' },
    userId: String, readOnly: Boolean, saving: Boolean, error: String,
    assignees: { type: Array, default: () => [] }, assigneesLoading: Boolean, assigneesError: String,
    modules: { type: Array, default: () => [] }
  },
  data: () => ({ mode: 'details', reason: '', assignedToId: null, assignmentNote: '' }),
  computed: {
    title() { return { details: 'Assignment Details', reject: 'Reject Assignment', assign: 'Reassign Action' }[this.mode]; },
    canRespond() { return !!this.action && canRespondToAssignment(this.action, this.userId); },
    canReassign() { return !!this.action && canReassignAction(this.action, this.userId); },
    rejectingUser() {
      const rejectedById = this.action?.rejectedById;
      if (!rejectedById) return null;
      const response = this.activityEvents.find(event => event.type === 'assignment_response' && (event.userId || event.user?.id) === rejectedById && event.user);
      return response?.user || this.assignees.find(user => user.id === rejectedById) || null;
    },
    relatedRecordLabel() { return this.action?.relatedRecordType === 'global' ? 'None' : this.action?.relatedRecordLabel || 'Not specified'; },
    actionModuleIds() { return this.action?.moduleIds?.length ? this.action.moduleIds : [this.action?.module].filter(Boolean); },
    assignmentColor() { return { accepted: 'success', awaiting_acceptance: 'orange darken-2', rejected: 'error' }[this.action && assignmentStatus(this.action)] || 'grey'; },
    detailDates() {
      const action = this.action || {};
      return [
        { label: 'Created', value: action.dateCreated },
        { label: 'Assigned', value: action.assignedAt },
        { label: 'Responded', value: action.respondedAt },
        { label: 'Last updated', value: action.dateUpdated },
        ...(action.completedAt ? [{ label: 'Completed', value: action.completedAt }] : []),
        ...(action.cancelledAt ? [{ label: 'Cancelled', value: action.cancelledAt }] : [])
      ];
    },
    activityEvents() {
      if (!this.action) return [];
      return [
        { id: `created-${this.action.id}`, type: 'created', user: this.action.createdBy, dateCreated: this.action.dateCreated, comment: 'Follow-up action created.' },
        ...(this.action.activity || [])
      ].sort((left, right) => (Date.parse(right.dateCreated) || 0) - (Date.parse(left.dateCreated) || 0));
    }
  },
  watch: {
    value(open) {
      if (open) { this.mode = this.initialMode; this.reason = ''; this.assignedToId = this.userId; this.assignmentNote = ''; }
    }
  },
  methods: {
    assignmentLabel,
    userName(user) { return user?.username || user?.email || 'User'; },
    moduleName(id) { return this.modules.find(module => module.id === id)?.name || id; },
    detailDate(date) { return date ? this.formatDate(date, 'MMM D, YYYY [at] h:mm A') : 'Not recorded'; },
    statusLabel(status) { return { pending: 'Pending', in_progress: 'In Progress', completed: 'Completed', cancelled: 'Cancelled' }[status] || status || 'Not specified'; },
    statusColor(status) { return { pending: 'blue-grey', in_progress: 'primary', completed: 'success', cancelled: 'grey darken-1' }[status] || 'grey'; },
    priorityColor(id) { return { low: 'blue-grey', medium: 'amber darken-2', high: 'deep-orange', urgent: 'red darken-2' }[id] || 'grey'; },
    activityLabel(type) { return { created: 'Action created', assignment_response: 'Assignment response', assignment_change: 'Assignment updated', status_change: 'Status updated' }[type] || 'Action updated'; },
    activityIcon(type) { return { created: 'mdi-plus-circle-outline', assignment_response: 'mdi-account-check-outline', assignment_change: 'mdi-account-arrow-right-outline', status_change: 'mdi-clipboard-check-outline' }[type] || 'mdi-history'; }
  }
};
</script>

<style scoped>
.assignment-text { white-space: pre-wrap; overflow-wrap: anywhere; }
.assignment-details-body { color: #344054 !important; }
.details-section { margin-bottom: 24px; }
.details-heading { color: #182230; font-size: 15px; font-weight: 600; margin-bottom: 12px; }
.action-summary { margin-bottom: 18px; padding: 16px; background: #f8fafc; border: 1px solid #e4e7ec; border-radius: 8px; }
.details-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px 24px; margin: 0; }
.detail-field { min-width: 0; }
.detail-field-wide { grid-column: 1 / -1; }
.detail-field dt { color: #667085; font-size: 12px; margin-bottom: 6px; }
.detail-field dd { margin: 0; font-size: 14px; overflow-wrap: anywhere; }
.detail-secondary { color: #667085; overflow-wrap: anywhere; }
.details-module-list { display: flex; flex-wrap: wrap; gap: 6px; }
.assignment-note { padding: 16px; border: 1px solid #d0d5dd; background: #f8fafc; border-radius: 8px; margin-top: 10px; }
.rejection-note { border-color: #fedf89; background: #fffaeb; }
.activity-section-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
.assignment-details-card .activity-list { list-style: none; margin: 0; padding: 0; display: grid; gap: 12px; }
.activity-entry { display: flex; gap: 12px; padding: 16px; border: 1px solid #e4e7ec; border-radius: 8px; background: #f8fafc; }
.activity-icon { display: flex; align-items: center; justify-content: center; flex: 0 0 32px; height: 32px; border: 1px solid #e4e7ec; border-radius: 50%; background: #fff; }
.activity-content { flex: 1; min-width: 0; }
.activity-entry-heading { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 4px 12px; }
.assignment-details-actions { gap: 8px; }
@media (max-width: 600px) {
  .details-grid { grid-template-columns: minmax(0, 1fr); }
  .assignment-details-card .assignment-details-body { padding: 16px !important; }
  .activity-entry { padding: 12px; }
}
</style>
